import { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Empty, Flex, Input, Row, Segmented, Typography, Upload } from 'antd';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { CalibrationPanel } from './CalibrationPanel';
import { PhysicalImage } from '../../components/PhysicalSize';
import { parseImageUrl } from '../../lib/image-url';

export type CompareMode = '3' | '5' | 'both' | 'original';

interface Candidate {
  id: number;
  name: string;
  src: string;
  /** 로컬 파일이면 해제해야 하는 object URL */
  objectUrl: boolean;
}

function nameFromUrl(src: string) {
  const last = new URL(src).pathname.split('/').filter(Boolean).pop();
  return last ? decodeURIComponent(last) : src;
}

function CandidateView({ c, mode }: { c: Candidate; mode: CompareMode }) {
  if (mode === 'original') return <PhysicalImage src={c.src} alt={c.name} size="original" />;
  if (mode === 'both') {
    return (
      <Flex gap={16} align="flex-end" wrap>
        <PhysicalImage src={c.src} alt={`${c.name} 3cm`} size={3} caption="3cm" />
        <PhysicalImage src={c.src} alt={`${c.name} 5cm`} size={5} caption="5cm" />
      </Flex>
    );
  }
  return <PhysicalImage src={c.src} alt={c.name} size={mode === '3' ? 3 : 5} />;
}

export function PreviewPage() {
  const [items, setItems] = useState<Candidate[]>([]);
  const [mode, setMode] = useState<CompareMode>('both');
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState(false);
  const nextId = useRef(1);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // 페이지를 떠날 때 로컬 파일 미리보기 URL을 해제한다.
  useEffect(() => () => itemsRef.current.forEach((c) => c.objectUrl && URL.revokeObjectURL(c.src)), []);

  const add = (c: Omit<Candidate, 'id'>) => setItems((prev) => [...prev, { ...c, id: nextId.current++ }]);
  const remove = (id: number) => setItems((prev) => {
    const hit = prev.find((c) => c.id === id);
    if (hit?.objectUrl) URL.revokeObjectURL(hit.src);
    return prev.filter((c) => c.id !== id);
  });
  const addUrl = () => {
    const src = parseImageUrl(url);
    if (!src) {
      setUrlError(true);
      return;
    }
    add({ name: nameFromUrl(src), src, objectUrl: false });
    setUrl('');
    setUrlError(false);
  };

  // 여러 후보를 나란히 보려면 실물 크기 상자를 같은 줄에 둔다(5cm·둘 다 보기는 넓은 칸).
  const span = mode === 'original' ? { xs: 24, md: 12 } : mode === 'both' ? { xs: 24, sm: 12, xl: 8 } : { xs: 12, sm: 8, md: 6, xl: 4 };

  return (
    <>
      <Typography.Title level={3}>실물 크기 검수</Typography.Title>
      <Typography.Paragraph type="secondary">
        이미지를 실제 카드 크기로 보여 작은 크기에서도 의미가 전달되는지 확인합니다. 3cm는 앱 카드 사용 크기, 5cm는 기존 실물 카드에 가까운 크기입니다.
      </Typography.Paragraph>

      <CalibrationPanel />

      <Card title="비교할 이미지" size="small" style={{ marginTop: 16 }}>
        <Flex gap={8} wrap align="flex-start">
          <Upload
            accept="image/png,image/jpeg,image/webp"
            multiple
            showUploadList={false}
            beforeUpload={(file) => {
              add({ name: file.name, src: URL.createObjectURL(file), objectUrl: true });
              return false; // 서버로 보내지 않고 이 브라우저에서만 표시
            }}
          >
            <Button icon={<UploadOutlined />}>내 컴퓨터에서 선택</Button>
          </Upload>
          <Input.Search
            placeholder="https://cdn.isori.app/images/..."
            enterButton="주소 추가"
            value={url}
            status={urlError ? 'error' : undefined}
            onChange={(e) => { setUrl(e.target.value); setUrlError(false); }}
            onSearch={addUrl}
            style={{ maxWidth: 520 }}
          />
        </Flex>
        {urlError && <Typography.Text type="danger" style={{ fontSize: 12 }}>https로 시작하는 이미지 주소를 입력해 주세요.</Typography.Text>}
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '8px 0 0' }}>
          선택한 파일은 업로드되지 않고 이 브라우저에서만 표시됩니다.
        </Typography.Paragraph>

        <Flex justify="space-between" align="center" wrap gap={8} style={{ margin: '16px 0' }}>
          <Segmented<CompareMode>
            value={mode}
            onChange={setMode}
            options={[
              { value: 'both', label: '3cm · 5cm' },
              { value: '3', label: '3cm' },
              { value: '5', label: '5cm' },
              { value: 'original', label: '원본' },
            ]}
          />
          {items.length > 0 && <Button size="small" onClick={() => items.forEach((c) => remove(c.id))}>모두 지우기</Button>}
        </Flex>

        {items.length === 0 ? (
          <Empty description="비교할 이미지를 추가하세요" />
        ) : (
          <Row gutter={[16, 16]}>
            {items.map((c) => (
              <Col key={c.id} {...span}>
                <Flex justify="space-between" align="center" gap={4} style={{ marginBottom: 6 }}>
                  <Typography.Text ellipsis={{ tooltip: c.name }} style={{ fontSize: 13 }}>{c.name}</Typography.Text>
                  <Button size="small" type="text" icon={<CloseOutlined />} aria-label={`${c.name} 빼기`} onClick={() => remove(c.id)} />
                </Flex>
                <CandidateView c={c} mode={mode} />
              </Col>
            ))}
          </Row>
        )}
      </Card>
    </>
  );
}
